import React, { useState } from 'react';
import { Check, Clock } from 'lucide-react';
import { DAYS_OF_WEEK, TIME_SLOT_CONFIGS, HOUR_RANGES } from '@tutor/types';

interface WeeklyTimeTableProps {
  selectedTimes: Record<string, string[]>;
  onChange: (selectedTimes: Record<string, string[]>) => void;
  customTimeRanges?: Record<string, { startHour: number; endHour: number }>;
  onCustomTimeRangesChange?: (ranges: Record<string, { startHour: number; endHour: number }>) => void;
  label?: string;
  helperText?: string;
  colorTheme?: 'primary' | 'secondary';
  isEditable?: boolean;
  dayLabels?: Partial<Record<string, string>>;
  slotLabels?: Partial<Record<string, string>>;
  timePickerCopy?: {
    titleTemplate?: string;
    descriptionTemplate?: string;
    startTimeLabel?: string;
    endTimeLabel?: string;
    cancelLabel?: string;
    saveLabel?: string;
  };
}

const WeeklyTimeTable: React.FC<WeeklyTimeTableProps> = ({
  selectedTimes,
  onChange,
  customTimeRanges: externalCustomTimeRanges,
  onCustomTimeRangesChange,
  label = 'Available Times',
  helperText = 'Click on the grid to select your preferred teaching times',
  colorTheme = 'primary',
  isEditable = true,
  dayLabels,
  slotLabels,
  timePickerCopy,
}) => {
  const themeColors = {
    primary: {
      selected: 'bg-primary text-white',
      hoverText: 'group-hover:text-primary',
      focusBorder: 'focus:border-primary',
      button: 'bg-primary text-white hover:bg-primary/90',
    },
    secondary: {
      selected: 'bg-primary text-white',
      hoverText: 'group-hover:text-secondary',
      focusBorder: 'focus:border-secondary',
      button: 'bg-secondary text-white hover:bg-secondary/90',
    },
  };

  const colors = themeColors[colorTheme];
  const resolvedTimePickerCopy = {
    titleTemplate: 'Set {slot} Time Range',
    descriptionTemplate: 'Scroll to select start and end times for {slot}',
    startTimeLabel: 'Start Time',
    endTimeLabel: 'End Time',
    cancelLabel: 'Cancel',
    saveLabel: 'Save',
    ...timePickerCopy,
  };
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<string | null>(null);
  const [tempStartHour, setTempStartHour] = useState(10);
  const [tempEndHour, setTempEndHour] = useState(11);
  const [internalCustomTimeRanges, setInternalCustomTimeRanges] = useState<Record<string, { startHour: number; endHour: number }>>({});
  const customTimeRanges = externalCustomTimeRanges ?? internalCustomTimeRanges;

  const getHourRange = (slotName: string) => {
    return (HOUR_RANGES as Record<string, { min: number; max: number }>)[slotName] || { min: 6, max: 23 };
  };

  const getSlotDisplayConfig = (slotName: string) => {
    const custom = customTimeRanges[slotName];
    const config = (TIME_SLOT_CONFIGS as Record<string, { startHour: number; endHour: number }>)[slotName];
    return {
      startHour: custom?.startHour ?? config.startHour,
      endHour: custom?.endHour ?? config.endHour,
    };
  };

  const openTimePicker = (slotName: string) => {
    setEditingSlot(slotName);
    const { startHour, endHour } = getSlotDisplayConfig(slotName);
    setTempStartHour(startHour);
    setTempEndHour(endHour);
    setIsTimePickerOpen(true);
  };

  const saveTimeSlot = (startHour: number, endHour: number) => {
    if (editingSlot) {
      const newRanges = { ...customTimeRanges, [editingSlot]: { startHour, endHour } };
      setInternalCustomTimeRanges(newRanges);
      onCustomTimeRangesChange?.(newRanges);
      setIsTimePickerOpen(false);
      setEditingSlot(null);
    }
  };

  const formatTime = (hour: number) => {
    const isPM = hour >= 12;
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}${isPM ? ' PM' : ' AM'}`;
  };

  const getDayLabel = (day: string) => dayLabels?.[day] ?? day.slice(0, 3);

  const getSlotLabel = (slotName: string) => slotLabels?.[slotName] ?? slotName;

  const toggleTimeSlot = (day: string, slotName: string) => {
    const daySlots = selectedTimes[day] || [];
    const newDaySlots = daySlots.includes(slotName)
      ? daySlots.filter((s) => s !== slotName)
      : [...daySlots, slotName];
    onChange({ ...selectedTimes, [day]: newDaySlots });
  };

  return (
    <>
      <div>
        {isEditable && (
          <label className="block text-sm font-medium text-gray-700 mb-3">{label}</label>
        )}
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-8 bg-gray-50">
            <div className="p-2 border-r border-b font-medium text-xs" />
            {DAYS_OF_WEEK.map((day) => (
              <div key={day} className="p-2 border-r border-b text-center font-medium text-xs">
                {getDayLabel(day)}
              </div>
            ))}
          </div>
          {Object.keys(TIME_SLOT_CONFIGS).map((slotName) => (
            <div key={slotName} className="grid grid-cols-8">
              {isEditable ? (
                <button
                  type="button"
                  onClick={() => openTimePicker(slotName)}
                  className="p-2 border-r border-b bg-gray-50 text-xs font-medium flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer group"
                >
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">{getSlotLabel(slotName)}</span>
                    <span className={`text-[10px] text-gray-500 ${colors.hoverText}`}>
                      {formatTime(getSlotDisplayConfig(slotName).startHour)}-
                      {formatTime(getSlotDisplayConfig(slotName).endHour)}
                    </span>
                  </div>
                  <Clock className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                </button>
              ) : (
                <div className="p-2 border-r border-b bg-gray-50 text-xs font-medium flex items-center">
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">{getSlotLabel(slotName)}</span>
                    <span className="text-[10px] text-gray-500">
                      {formatTime(getSlotDisplayConfig(slotName).startHour)}-
                      {formatTime(getSlotDisplayConfig(slotName).endHour)}
                    </span>
                  </div>
                </div>
              )}
              {DAYS_OF_WEEK.map((day) => {
                const isSelected = selectedTimes[day]?.includes(slotName);
                return (
                  <button
                    key={`${day}-${slotName}`}
                    type="button"
                    onClick={() => isEditable && toggleTimeSlot(day, slotName)}
                    className={`p-3 border-r border-b transition-all duration-150 ease-in-out ${
                      isSelected ? colors.selected : 'bg-white'
                    } ${isEditable && !isSelected ? 'hover:bg-primary/5' : ''} ${isEditable ? 'active:scale-95' : 'cursor-default'}`}
                    disabled={!isEditable}
                  >
                    <span className={`transition-opacity duration-150 ${isSelected ? 'opacity-100' : 'opacity-0'}`}>
                      <Check className="w-4 h-4 mx-auto" />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {isEditable && <p className="text-xs text-gray-500 mt-2">{helperText}</p>}
      </div>

      {isEditable && isTimePickerOpen && editingSlot && (
        <div
          className="fixed inset-0 z-[1030] flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsTimePickerOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">
              {resolvedTimePickerCopy.titleTemplate.replace('{slot}', getSlotLabel(editingSlot))}
            </h3>
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                {resolvedTimePickerCopy.descriptionTemplate.replace('{slot}', getSlotLabel(editingSlot))}
              </p>
              <div className="flex items-center justify-center space-x-4">
                <div className="flex flex-col items-center">
                  <label className="text-xs font-medium text-gray-600 mb-2">
                    {resolvedTimePickerCopy.startTimeLabel}
                  </label>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                    <select
                      value={tempStartHour}
                      onChange={(e) => {
                        const newStart = parseInt(e.target.value);
                        setTempStartHour(newStart);
                        if (newStart >= tempEndHour) setTempEndHour(newStart + 1);
                      }}
                      className={`h-32 px-4 text-center text-lg font-medium focus:outline-none ${colors.focusBorder} cursor-pointer`}
                      style={{ fontSize: '1.25rem' }}
                    >
                      {Array.from(
                        { length: getHourRange(editingSlot).max - getHourRange(editingSlot).min + 1 },
                        (_, i) => getHourRange(editingSlot).min + i
                      ).map((hour) => (
                        <option key={hour} value={hour}>{formatTime(hour)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-400 pt-8">-</div>
                <div className="flex flex-col items-center">
                  <label className="text-xs font-medium text-gray-600 mb-2">
                    {resolvedTimePickerCopy.endTimeLabel}
                  </label>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                    <select
                      value={tempEndHour}
                      onChange={(e) => setTempEndHour(parseInt(e.target.value))}
                      className={`h-32 px-4 text-center text-lg font-medium focus:outline-none ${colors.focusBorder} cursor-pointer`}
                      style={{ fontSize: '1.25rem' }}
                    >
                      {Array.from(
                        { length: getHourRange(editingSlot).max - tempStartHour },
                        (_, i) => tempStartHour + i + 1
                      ).map((hour) => (
                        <option key={hour} value={hour}>{formatTime(hour)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setIsTimePickerOpen(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                {resolvedTimePickerCopy.cancelLabel}
              </button>
              <button
                onClick={() => saveTimeSlot(tempStartHour, tempEndHour)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${colors.button}`}
              >
                {resolvedTimePickerCopy.saveLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeeklyTimeTable;
